import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PlatformPicker, PrimaryButton, GhostButton, TextArea } from "@/components/StreamBox";
import VoiceButton from "@/components/VoiceButton";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Twitter, Flame } from "lucide-react";

export default function Score() {
  const navigate = useNavigate();
  const { refresh: refreshSub } = useSubscription();
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [platform, setPlatform] = useState("twitter");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [animatedScore, setAnimatedScore] = useState(0);
  const animRef = useRef(null);

  useEffect(() => () => animRef.current && cancelAnimationFrame(animRef.current), []);

  const animateTo = (target) => {
    const start = performance.now();
    const dur = 1200;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimatedScore(Math.round(target * eased));
      if (p < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  };

  const run = async () => {
    if (!content.trim() || content.length < 5) return toast.error("Paste content first.");
    setLoading(true);
    setResult(null);
    setAnimatedScore(0);
    try {
      const res = await api.post("/generate/score", { content, platform });
      setResult(res.data);
      animateTo(res.data.score ?? 0);
      refreshSub();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Free daily limit hit. Upgrade to Pro for unlimited.", {
          action: { label: "Upgrade", onClick: () => navigate("/app/pricing") },
        });
      } else {
        toast.error(e?.response?.data?.detail || "Score failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!result) return;
    try {
      await api.post("/library", {
        type: "score",
        platform,
        input: content,
        output: JSON.stringify(result),
        score: result.score,
      });
      toast.success("Saved to Vault.");
    } catch (e) { toast.error("Save failed."); }
  };

  const scoreColor =
    animatedScore >= 80 ? "text-signal-yellow" :
    animatedScore >= 60 ? "text-white" :
    animatedScore >= 40 ? "text-zinc-400" : "text-signal-red";

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-8">
      <header className="border-b border-white/10 pb-6">
        <div className="label-eyebrow">// module 03</div>
        <h1 className="heading-display text-4xl sm:text-5xl mt-2">SCORE <span className="text-signal-yellow">ENGINE</span></h1>
        <p className="font-mono text-xs text-zinc-500 mt-3">// brutally honest 0–100 virality rating with actionable fixes.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <TextArea
            data-testid="score-content"
            label="Content to score"
            placeholder="Paste your draft post, script, or tweet here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <VoiceButton testid="score-voice" label="Voice input" onText={(t) => setContent((p) => (p ? p + "\n" + t : t))} />
          <div className="space-y-2">
            <span className="label-eyebrow block">Platform</span>
            <PlatformPicker value={platform} onChange={setPlatform} name="score-platform" />
          </div>
          <PrimaryButton data-testid="score-run" onClick={run} disabled={loading} className="w-full">
            {loading ? "Analyzing..." : "Run virality scan →"}
          </PrimaryButton>
        </div>

        <div className="brutal-border bg-[#0A0A0A]">
          <div className="px-4 py-2 border-b border-white/10 bg-[#141414] flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400">Virality Diagnostic</span>
            <span className="font-mono text-[10px] text-zinc-600">{loading ? "ANALYZING" : result ? "COMPLETE" : "IDLE"}</span>
          </div>
          <div className="p-6 space-y-6 min-h-[400px]">
            {!result && !loading && (
              <div className="font-mono text-xs text-zinc-600">// awaiting input.</div>
            )}
            {loading && (
              <div className="font-mono text-xs text-zinc-400 animate-pulse">// analyzing pattern interrupt, retention curve, share-trigger density...</div>
            )}
            {result && (
              <>
                <div data-testid="score-value" className={`font-mono font-bold text-[18vw] md:text-[10vw] leading-none ${scoreColor}`}>
                  {animatedScore}
                  <span className="text-zinc-700 text-2xl ml-2">/100</span>
                </div>
                {result.verdict && (
                  <p data-testid="score-verdict" className="font-mono text-sm text-zinc-200 leading-relaxed border-l-2 border-signal-yellow pl-4">
                    {result.verdict}
                  </p>
                )}

                {Array.isArray(result.strengths) && result.strengths.length > 0 && (
                  <div data-testid="score-strengths">
                    <div className="label-eyebrow mb-2 text-signal-yellow">// strengths</div>
                    <ul className="space-y-1">
                      {result.strengths.map((s, i) => (
                        <li key={i} className="font-mono text-xs text-zinc-300 leading-relaxed">↳ {s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(result.weaknesses) && result.weaknesses.length > 0 && (
                  <div data-testid="score-weaknesses">
                    <div className="label-eyebrow mb-2 text-signal-red">// weaknesses</div>
                    <ul className="space-y-1">
                      {result.weaknesses.map((s, i) => (
                        <li key={i} className="font-mono text-xs text-zinc-300 leading-relaxed">↳ {s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(result.fixes) && result.fixes.length > 0 && (
                  <div data-testid="score-fixes">
                    <div className="label-eyebrow mb-2">// fixes</div>
                    <ul className="space-y-1">
                      {result.fixes.map((s, i) => (
                        <li key={i} className="font-mono text-xs text-zinc-300 leading-relaxed">→ {s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="pt-4 flex flex-wrap gap-2">
                  <GhostButton data-testid="score-save" onClick={save}><Save size={12} className="inline mr-2" />Save to Vault</GhostButton>
                  {result.score >= 85 && (
                    <ShareToX content={content} refCode={user?.referral_code} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareToX({ content, refCode }) {
  const link = refCode ? `${window.location.origin}/auth?ref=${refCode}` : window.location.origin;
  const truncated = content.length > 200 ? content.slice(0, 197) + "..." : content;
  const tweet = `${truncated}\n\n(scored 85+ on Viral/Co — grab 30 days free: ${link})`;
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="score-share-x"
      className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-4 py-2 hover:bg-signal-yellow-hover transition-colors inline-flex items-center gap-2 border border-signal-yellow animate-pulse"
    >
      <Flame size={12} /> <Twitter size={12} /> Share this to X →
    </a>
  );
}
